import { useRef } from "react";
import {
	DndContext,
	closestCenter,
	useSensor,
	useSensors,
	MouseSensor,
	TouchSensor,
    KeyboardSensor
} from "@dnd-kit/core";
import {
	SortableContext,
	useSortable,
	arrayMove,
	verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { PhotoItem } from "../../types";

function SortablePhotoRow({
	id,
	item,
	onCaptionChange,
	onDelete
}: {
	id: string;
	item: PhotoItem;
	onCaptionChange: (caption: string) => void;
	onDelete: () => void;
}) {
	const { setNodeRef, attributes, listeners, transform, transition } =
		useSortable({ id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition: transition?.replace(/height|width|top|left/g,"")
	};
	return (
		<div
			ref={setNodeRef}
			style={style}
			className="d-flex gap-3 align-items-start border rounded p-2"
		>
			<span
				className="drag-handle me-2"
				title="Drag to reorder"
				{...attributes}
				{...listeners}
			>
				☰
			</span>

			<img
				src={item.url ?? URL.createObjectURL(item.file)}
				width={100}
				height={100}
				className="rounded border"
				alt=""
				onLoad={(e) =>
					URL.revokeObjectURL(
						(e.currentTarget as HTMLImageElement).src
					)
				}
			/>

			<div className="flex-grow-1">
				<input
                    id = {item.id + " caption"}
					type="text"
					className="form-control mb-2"
					placeholder="Caption"
					value={item.caption ?? ""}
					onChange={(e) => onCaptionChange(e.currentTarget.value)}
				/>

				<button
					type="button"
					className="btn btn-sm btn-outline-danger"
					onClick={onDelete}
				>
					Delete
				</button>
			</div>
		</div>
	);
}

export function PhotoField({
	fieldKey,
	required,
	maxFiles = 5,
	value = [],
	onChange
}: {
	fieldKey: number;
	required?: boolean;
	maxFiles?: number;
	value?: PhotoItem[];
	onChange: (files: PhotoItem[]) => void;
}) {
	const inputRef = useRef<HTMLInputElement>(null);
	const limit = Math.min(maxFiles, 10);
	const sensors = useSensors(
        useSensor(KeyboardSensor), 
		useSensor(MouseSensor, { activationConstraint: { distance: 5 }}),
		useSensor(TouchSensor, { activationConstraint: { delay: 400, tolerance: 5 }})
	);
	return (
		<div className="photo-field">
			<span
				className="btn btn-primary mb-2"
				onClick={() => inputRef.current?.click()}
			>
				Upload Photos
			</span>

			<input
				ref={inputRef}
				type="file"
				id={String(fieldKey)}
				accept="image/*"
				multiple
				required={required}
				style={{ display: "none" }}
				onChange={(e) => {
					const input = e.currentTarget;
					if (!input.files) return;

					const newItems = Array.from(input.files).map((file) => ({
                        id: crypto.randomUUID(), 
						file,
						caption: ""
					}));

					const merged = [...value, ...newItems];

					if (merged.length > limit) {
						alert(`Max ${limit} images allowed`);
						input.value = "";
						return;
					}

					onChange(merged);
					input.value = "";
				}}
			/>

			{value.length > 0 && (
				<DndContext
					sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => {
                        const { active, over } = event;
                        if (!over || active.id === over.id) return;
                        const oldIndex = value.findIndex((v) => v.id === active.id);
                        const newIndex = value.findIndex((v) => v.id === over.id);
                        onChange(arrayMove(value, oldIndex, newIndex));
					}}
				>
					<SortableContext
						items={value.map((item) => item.id)}
						strategy={verticalListSortingStrategy}
					>
						<div className="d-flex flex-column gap-3 mt-2">
							{value.map((item, i) => (
								<SortablePhotoRow
									key={item.id}
									id={item.id}
									item={item}
									onCaptionChange={(caption) => {
										const next = [...value];
										next[i] = { ...next[i], caption };
										onChange(next);
									}}
									onDelete={() =>
										onChange(
											value.filter((_, idx) => idx !== i)
										)
									}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
			)}
		</div>
	);
}